import type { WidgetConfig } from './types';
import { Widget } from './ui/widget';

/**
 * Resolve config from the loading <script> tag's data-* attributes, e.g.:
 *   <script src="widget.js" data-project="pk_..." data-api="https://api.example.com/api/v1"></script>
 * Falls back to a global FeedbackProConfig object if present.
 */
function resolveConfig(): WidgetConfig | null {
  const global = (window as unknown as { FeedbackProConfig?: Partial<WidgetConfig> }).FeedbackProConfig;

  const script =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>('script[data-project]');

  const projectKey = global?.projectKey ?? script?.dataset.project ?? '';
  if (!projectKey) return null;

  const apiBase =
    global?.apiBase ??
    script?.dataset.api ??
    `${location.protocol}//${location.host}/api/v1`;

  return {
    projectKey,
    apiBase: apiBase.replace(/\/$/, ''),
    reviewerName: global?.reviewerName,
    reviewerEmail: global?.reviewerEmail,
    sessionToken: resolveSessionToken(projectKey),
    focusPinId: takeQueryParam('fp_pin'),
  };
}

/** Read a query param then strip it from the URL/history. */
function takeQueryParam(name: string): string | undefined {
  try {
    const url = new URL(window.location.href);
    const value = url.searchParams.get(name);
    if (value) {
      url.searchParams.delete(name);
      window.history.replaceState({}, '', url.toString());
      return value;
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

/**
 * Capture a client feedback-session token. It rides the URL once
 * (?fp_session=…) on launch; we persist it per-origin+project in localStorage
 * so it survives navigation, then strip it from the URL/history.
 */
function resolveSessionToken(projectKey: string): string | undefined {
  const storageKey = `fp_session:${projectKey}`;
  let token: string | null = null;

  try {
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams.get('fp_session');
    if (fromUrl) {
      token = fromUrl;
      window.localStorage.setItem(storageKey, fromUrl);
      // Remove the token from the visible URL + history.
      url.searchParams.delete('fp_session');
      window.history.replaceState({}, '', url.toString());
    } else {
      token = window.localStorage.getItem(storageKey);
    }
  } catch {
    // localStorage / URL unavailable — degrade to no session.
  }

  return token ?? undefined;
}

function boot(): void {
  const config = resolveConfig();
  if (!config) return; // No key → silently do nothing.

  const widget = new Widget(config);
  // Fail quietly: a widget error must never break the host page.
  void widget.start().catch(() => {});
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

export { Widget };
